import { Static, Type } from '@sinclair/typebox';
import { TrialRelanceMoment, TrialSegment, TrialVoie } from '../translation_namespace/trial_relance_namespace';

/**
 * Admin — relance des essais (plan §6 « Écran Relances » et « Liste des pros en essai »).
 * Routes `app/admin/trial-relance/*`, JWT admin, scopes CRM_READ / CRM_WRITE.
 */

/** Ligne de la liste des pros en essai. */
export const AdminTrialProItemSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  email: Type.Optional(Type.String()),
  trial_day: Type.Number(),
  days_left: Type.Number(),
  trial_end_date_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'trial_end_date' } })),
  pre_wizard: Type.Boolean(),
  voie: Type.Union([Type.Enum(TrialVoie), Type.Null()]),
  score: Type.Number(),
  stage_reached: Type.Number(),
  next_stage: Type.Union([Type.Number(), Type.Null()]),
  segment: Type.Enum(TrialSegment),
  /** Libellé (clé `TRIAL_RELANCE`) de la sous-action visée, null quand tout est fait. */
  next_action_label_key: Type.Union([Type.String(), Type.Null()]),
  last_relance_moment: Type.Union([Type.String(), Type.Null()]),
  last_relance_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_relance_at', with_time: true } }),
  ),
  /** Prochain moment du calendrier non encore envoyé (null si la séquence est finie). */
  next_moment: Type.Union([Type.String(), Type.Null()]),
  paused: Type.Boolean(),
  abandoned: Type.Boolean(),
  opted_out: Type.Boolean(),
  /** Relances suspendues : la personne est dans la file d'appels du CRM. */
  in_call_queue: Type.Optional(Type.Boolean()),
  /** Engagement PostHog (lot 2) quand disponible. */
  sessions: Type.Optional(Type.Number()),
  minutes: Type.Optional(Type.Number()),
});

export const AdminTrialProListResponseSchema = Type.Object({
  items: Type.Array(AdminTrialProItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

/** Une relance envoyée (ou consommée sans envoi). */
export const AdminTrialNotificationItemSchema = Type.Object({
  trial_relance_notification_id: Type.Number(),
  professional_id: Type.Number(),
  professional_name: Type.String(),
  moment: Type.String(),
  voie: Type.Union([Type.String(), Type.Null()]),
  stage: Type.Union([Type.Number(), Type.Null()]),
  angle: Type.Union([Type.Number(), Type.Null()]),
  segment: Type.Union([Type.String(), Type.Null()]),
  content_key: Type.String(),
  channels: Type.Array(Type.String()),
  subject: Type.Union([Type.String(), Type.Null()]),
  sent_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  /** `skipped` = moment consommé sans envoi (rien à dire pour ce pro). */
  skipped: Type.Boolean(),
});

export const AdminTrialNotificationListResponseSchema = Type.Object({
  items: Type.Array(AdminTrialNotificationItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

export const AdminTrialNotificationDetailSchema = Type.Composite([
  AdminTrialNotificationItemSchema,
  Type.Object({
    body_text: Type.Union([Type.String(), Type.Null()]),
    cta_url: Type.Union([Type.String(), Type.Null()]),
    /** Statuts `notification_log` du même jour pour ce pro et cet event (SENT / FAILED / SKIPPED…). */
    delivery: Type.Array(
      Type.Object({ channel: Type.String(), status: Type.String(), error: Type.Optional(Type.String()) }),
    ),
  }),
]);

/** Relance prévue (calculée à la volée : ce que le cron enverra si l'état du pro ne change pas). */
export const AdminTrialUpcomingItemSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.String(),
  moment: Type.String(),
  planned_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'planned_at', with_time: true } }),
  pre_wizard: Type.Boolean(),
  voie: Type.Union([Type.Enum(TrialVoie), Type.Null()]),
  segment: Type.Enum(TrialSegment),
  stage: Type.Union([Type.Number(), Type.Null()]),
  next_action_label_key: Type.Union([Type.String(), Type.Null()]),
  /** Séquence en pause / stoppée / désabonnée : le moment est listé mais ne partira pas. */
  blocked: Type.Boolean(),
});

export const AdminTrialUpcomingListResponseSchema = Type.Object({
  items: Type.Array(AdminTrialUpcomingItemSchema),
  total: Type.Number(),
  horizon_days: Type.Number(),
});

/** Pourquoi une relance ne partirait pas, telle qu'affichée dans l'aperçu admin. */
export enum AdminTrialPreviewBlockReason {
  /** Pro interne, migré, en pause, séquence stoppée ou désabonné des emails de cycle de vie. */
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  /** Ce moment n'a rien à dire à ce pro (ex. SMS J2 réservé aux Fantômes). */
  NO_CONTENT = 'NO_CONTENT',
  /** Contenu rendu mais aucun canal ouvert (pas de téléphone, email en rebond dur). */
  NO_CHANNEL = 'NO_CHANNEL',
}

export const AdminTrialPreviewEmailSchema = Type.Object({
  /** Adresse du pro, null quand le compte n'en a pas. */
  to: Type.Union([Type.String(), Type.Null()]),
  from: Type.String(),
  from_name: Type.String(),
  reply_to: Type.Union([Type.String(), Type.Null()]),
  subject: Type.String(),
  /** HTML final, template compris — exactement ce que Mailjet recevra. */
  html: Type.String(),
});

export const AdminTrialPreviewSmsSchema = Type.Object({
  to: Type.Union([Type.String(), Type.Null()]),
  sender: Type.String(),
  body: Type.String(),
  characters: Type.Number(),
  segments: Type.Number(),
  /** GSM7 ou UCS2, après l'optimisation d'encodage appliquée à l'envoi. */
  encoding: Type.String(),
});

export const AdminTrialPreviewPushSchema = Type.Object({
  title: Type.String(),
  body: Type.String(),
});

/**
 * Aperçu d'un moment pour un pro : le message rendu par le MÊME chemin que l'envoi, sans rien
 * consommer ni envoyer. Les blocs sont rendus même quand le canal est fermé (`channels` dit ce
 * qui partirait réellement).
 */
export const AdminTrialRelancePreviewSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.String(),
  moment: Type.String(),
  content_key: Type.Union([Type.String(), Type.Null()]),
  voie: Type.Union([Type.Enum(TrialVoie), Type.Null()]),
  stage: Type.Union([Type.Number(), Type.Null()]),
  angle: Type.Number(),
  segment: Type.Enum(TrialSegment),
  /** Canaux qui partiraient réellement (corps présent ET canal ouvert pour ce pro). */
  channels: Type.Array(Type.String()),
  would_send: Type.Boolean(),
  block_reason: Type.Union([Type.Enum(AdminTrialPreviewBlockReason), Type.Null()]),
  email: Type.Union([AdminTrialPreviewEmailSchema, Type.Null()]),
  sms: Type.Union([AdminTrialPreviewSmsSchema, Type.Null()]),
  push: Type.Union([AdminTrialPreviewPushSchema, Type.Null()]),
  in_app: Type.Union([AdminTrialPreviewPushSchema, Type.Null()]),
  cta_url: Type.Union([Type.String(), Type.Null()]),
});

export enum AdminTrialRelanceControlAction {
  PAUSE = 'PAUSE',
  RESUME = 'RESUME',
  ABANDON = 'ABANDON',
}

export const AdminTrialRelanceControlBodySchema = Type.Object({
  action: Type.Enum(AdminTrialRelanceControlAction),
});

export const AdminTrialRelanceControlResponseSchema = Type.Object({
  professional_id: Type.Number(),
  paused: Type.Boolean(),
  abandoned: Type.Boolean(),
});

export const AdminTrialRelanceResendResponseSchema = Type.Object({
  sent: Type.Boolean(),
  moment: Type.String(),
});

/** Bloc « relance d'essai » de la fiche 360. */
export const Admin360TrialRelanceSchema = Type.Object({
  trial_day: Type.Number(),
  days_left: Type.Number(),
  pre_wizard: Type.Boolean(),
  voie: Type.Union([Type.Enum(TrialVoie), Type.Null()]),
  score: Type.Number(),
  stage_reached: Type.Number(),
  next_stage: Type.Union([Type.Number(), Type.Null()]),
  segment: Type.Enum(TrialSegment),
  next_action_label_key: Type.Union([Type.String(), Type.Null()]),
  last_relance_moment: Type.Union([Type.String(), Type.Null()]),
  next_moment: Type.Union([Type.String(), Type.Null()]),
  paused: Type.Boolean(),
  abandoned: Type.Boolean(),
  opted_out: Type.Boolean(),
  /** Relances suspendues : la personne est dans la file d'appels du CRM. */
  in_call_queue: Type.Optional(Type.Boolean()),
  sessions: Type.Optional(Type.Number()),
  minutes: Type.Optional(Type.Number()),
});

export const TRIAL_RELANCE_MOMENT_ORDER: readonly TrialRelanceMoment[] = [
  TrialRelanceMoment.J0_SMS,
  TrialRelanceMoment.J1,
  TrialRelanceMoment.J2,
  TrialRelanceMoment.J2_SMS,
  TrialRelanceMoment.J3,
  TrialRelanceMoment.J5,
  TrialRelanceMoment.J6,
  TrialRelanceMoment.J6_SMS,
  TrialRelanceMoment.J7,
];

export type AdminTrialProItem = Static<typeof AdminTrialProItemSchema>;
export type AdminTrialProListResponse = Static<typeof AdminTrialProListResponseSchema>;
export type AdminTrialNotificationItem = Static<typeof AdminTrialNotificationItemSchema>;
export type AdminTrialNotificationListResponse = Static<typeof AdminTrialNotificationListResponseSchema>;
export type AdminTrialNotificationDetail = Static<typeof AdminTrialNotificationDetailSchema>;
export type AdminTrialUpcomingItem = Static<typeof AdminTrialUpcomingItemSchema>;
export type AdminTrialUpcomingListResponse = Static<typeof AdminTrialUpcomingListResponseSchema>;
export type AdminTrialRelanceControlBody = Static<typeof AdminTrialRelanceControlBodySchema>;
export type AdminTrialRelanceControlResponse = Static<typeof AdminTrialRelanceControlResponseSchema>;
export type AdminTrialRelanceResendResponse = Static<typeof AdminTrialRelanceResendResponseSchema>;
export type Admin360TrialRelance = Static<typeof Admin360TrialRelanceSchema>;
export type AdminTrialPreviewEmail = Static<typeof AdminTrialPreviewEmailSchema>;
export type AdminTrialPreviewSms = Static<typeof AdminTrialPreviewSmsSchema>;
export type AdminTrialPreviewPush = Static<typeof AdminTrialPreviewPushSchema>;
export type AdminTrialRelancePreview = Static<typeof AdminTrialRelancePreviewSchema>;
